import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  parsePostCardPath,
  useContentScriptsNew,
  useContentBeforeAfterGalleriesNew,
  useContentRecommendedOffersNew,
  useContentCollapsersNew,
  useContentAdfoxBannersNew,
  useContentAuthorOpinionDisclaimer,
  useContentYoutubeVideosNew,
  useContentJournalBannersNew,
  useContentDealsBannerNew,
  useContentBlockquotesNew,
  useContentPhotoGalleriesNew,
  useContentExpertBlocks,
  useContentImages,
  canDeleteAllImagesFromPosts,
  getDealsBannerText,
  ApplicationContext,
} from '../../utils';
import { setPostAttribute } from '../../actions/post';
import { setPostPreviewAttribute } from '../../actions/postPreview';
import { ERequestStatus } from '../../types/requestStatus';
import { EType } from '../../repositories/journal/entities/journal/JournalAttributesSchema';
import { TDealsBannerConfig } from '../../types/dealsBannerConfig';
import { TPostState } from '../../types/post';
import { TPostPreviewState } from '../../types/postPreview';

/** Шаги последовательного пайплайна парсинга контента статьи */
enum EPrepareContentStep {
  Idle = 0,
  Scripts = 1,
  RecommendedOffers = 2,
  Collapsers = 3,
  BeforeAfterGalleries = 4,
  AdfoxBanners = 5,
  YoutubeVideos = 6,
  JournalBanners = 7,
  Blockquotes = 8,
  PhotoGalleries = 9,
  ExpertBlocks = 10,
  Images = 11,
  AuthorOpinionDisclaimer = 12,
  DealsBanner = 13,
}

/**
 * Рендерит вставки в контент поста
 */
export const usePreparedContent = (post: TPostState | TPostPreviewState, isDealsExperimentGroupB: boolean) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [step, setStep] = useState<EPrepareContentStep>(EPrepareContentStep.Idle);
  const { id: qsPostId } = parsePostCardPath(pathname);
  const [canRender, setCanRender] = useState(false);
  const [postContent, setPostContent] = useState('');
  const context = useContext(ApplicationContext);
  const isPostPreview = 'id' in post && post.id === 0;

  /**
   * Если статья входит в А/Б тест баннера/формы Сделки, фичетогл включён и
   * пользователь попал в тестовую группу B, отключает показ рекламных и
   * редакторских баннеров в контенте статьи — они не должны конкурировать с
   * баннером/формой Сделки. В группе A (контрольной) сторонние баннеры
   * остаются как есть — сама фича для группы A не показывается
   */
  const isDealsBannerExperimentArticle = Boolean(
    context.custom.isDealsBannerExperimentEnabled &&
    isDealsExperimentGroupB &&
    'id' in post &&
    getDealsBannerText(post.id, context.config.get<TDealsBannerConfig>('frontend-content.dealsBannerConfig')),
  );

  /** Сбрасывает состояния при переходе с поста на пост */
  useEffect(() => {
    if (pathname === prevPathname) {
      return;
    }

    setStep(EPrepareContentStep.Idle);
    setPrevPathname(pathname);
    setCanRender(false);
    setPostContent('');
  }, [pathname, prevPathname]);

  /** Запускает парсинг, когда пост загружен */
  useEffect(() => {
    if (!('id' in post) || post.id !== qsPostId || post.status !== ERequestStatus.Succeed || step) {
      return;
    }

    setStep(EPrepareContentStep.Scripts);
    setPostContent(post.attributes.preparedContent || post.attributes.content || '');
  }, [post, qsPostId, step]);

  /** Запускает рендеринг, когда пройдены все этапы парсинга */
  useEffect(() => {
    if (step <= EPrepareContentStep.DealsBanner) {
      return;
    }

    setCanRender(true);
  }, [step]);

  /** Сохраняет в стор изменения в контексте поста */
  useEffect(() => {
    if (!postContent) {
      return;
    }

    if (isPostPreview) {
      dispatch(setPostPreviewAttribute({ key: 'preparedContent', value: postContent }));
    } else {
      dispatch(setPostAttribute({ key: 'preparedContent', value: postContent }));
    }
  }, [dispatch, isPostPreview, postContent]);

  /** Переключает на следующий этап */
  const nextStep = useCallback(() => {
    setStep(step => step + 1);
  }, []);

  useContentScriptsNew({
    canParse: step === EPrepareContentStep.Scripts,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
  });
  useContentRecommendedOffersNew({
    canParse: step === EPrepareContentStep.RecommendedOffers,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    isPostPreview,
  });
  useContentCollapsersNew({
    canParse: step === EPrepareContentStep.Collapsers,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    isPostPreview,
  });
  useContentBeforeAfterGalleriesNew({
    canParse: step === EPrepareContentStep.BeforeAfterGalleries,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
  });
  useContentAdfoxBannersNew({
    canParse: step === EPrepareContentStep.AdfoxBanners,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    options: { isEnabled: !isDealsBannerExperimentArticle },
  });
  useContentYoutubeVideosNew({
    canParse: step === EPrepareContentStep.YoutubeVideos,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
  });
  useContentJournalBannersNew({
    canParse: step === EPrepareContentStep.JournalBanners,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    isPostPreview,
    options: { isEnabled: !isDealsBannerExperimentArticle },
  });
  useContentBlockquotesNew({
    canParse: step === EPrepareContentStep.Blockquotes,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
  });
  useContentPhotoGalleriesNew({
    canParse: step === EPrepareContentStep.PhotoGalleries,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
  });
  useContentExpertBlocks({
    canParse: step === EPrepareContentStep.ExpertBlocks,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
  });
  useContentImages({
    canParse: step === EPrepareContentStep.Images,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    options: {
      isEnabled: canDeleteAllImagesFromPosts(
        new Date('id' in post ? post.attributes.datePublish : ''),
        context.custom.deleteAllImagesFromPostsSettings,
      ),
    },
  });
  useContentAuthorOpinionDisclaimer({
    canParse: step === EPrepareContentStep.AuthorOpinionDisclaimer,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    options: {
      isEnabled: 'id' in post && post.attributes.type === EType.Blogs,
    },
  });
  const dealsBannerContainerId = useContentDealsBannerNew({
    canParse: step === EPrepareContentStep.DealsBanner,
    postContent,
    setPostContent,
    done: nextStep,
    options: { isEnabled: isDealsBannerExperimentArticle },
  });

  return { dealsBannerContainerId };
};
